import {useEffect, useRef} from 'react'
import {Banner, Link} from '@primer/react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useLayoutQuery} from '../../page-data/loaders/use-layout-query'

export type StackCreatedInfo = {
  stackNumber: number
  appended: boolean
}

export type StackCreatedBannerProps = {
  info: StackCreatedInfo
  onDismiss: () => void
}

export function StackCreatedBanner({info, onDismiss}: StackCreatedBannerProps) {
  const {repository} = useLayoutQuery().data
  const stackText = `${info.appended ? 'stack' : 'Stack'} #${info.stackNumber}`
  const stackReference = isFeatureEnabled('pull_requests_stacks_view_ui') ? (
    <Link inline href={`/${repository.ownerLogin}/${repository.name}/stack/${info.stackNumber}`}>
      {stackText}
    </Link>
  ) : (
    stackText
  )
  const bannerText = info.appended ? (
    <>This pull request was added to {stackReference}</>
  ) : (
    <>{stackReference} was created successfully</>
  )
  const labelText = `Stack ${info.appended ? 'Updated' : 'Created'} Banner`
  const bannerRef = useRef<HTMLDivElement>(null)

  // On mount, move focus to the banner so screen readers announce the
  // banner's message (via `aria-label`) and keyboard users have a stable
  // focus target after the "Preview stack" button that triggered the flow
  // unmounts. `tabIndex={-1}` keeps the wrapper out of the tab sequence
  // afterwards — from here Tab reaches the banner's link and dismiss button.
  useEffect(() => {
    bannerRef.current?.focus()
  }, [])

  return (
    <Banner
      ref={bannerRef}
      aria-label={labelText}
      title={labelText}
      hideTitle
      description={bannerText}
      variant="success"
      onDismiss={onDismiss}
      tabIndex={-1}
    />
  )
}
